/**
 * Recovering from a deploy while the app is open.
 *
 * Each release is a new Docker image holding only its own hashed chunks. A tab opened before the
 * deploy still runs the old build, and the first time it needs a chunk it hasn't loaded yet (a
 * dynamic `import()`, or a dependency Vite preloads for one) the request 404s. The fix is one full
 * page load: the browser fetches the new `index.html` and the user lands on the new build.
 *
 * The reload goes to `window.location.href`, which is already the page the user was heading to:
 * react-router updates the URL before the page that needs the chunk renders.
 *
 * A reload loop is impossible by construction: if we reloaded for this less than
 * `RELOAD_GUARD_MS` ago, the new build is broken too (or the chunk really is missing), so we give
 * up and let the error surface instead of reloading forever.
 */

const RELOAD_AT_KEY = 'chunk-reload-at';
export const RELOAD_GUARD_MS = 60_000;

// Browsers word this differently: Chrome, Firefox, Safari, and Vite's own preload helper.
const CHUNK_ERROR =
    /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Unable to preload CSS|Failed to load module script/i;

export const isChunkLoadError = (error: unknown) =>
    CHUNK_ERROR.test(error instanceof Error ? error.message : String(error ?? ''));

const readReloadedAt = () => {
    try {
        return Number(window.sessionStorage.getItem(RELOAD_AT_KEY)) || 0;
    } catch {
        return 0;
    }
};

const writeReloadedAt = (time: number) => {
    try {
        window.sessionStorage.setItem(RELOAD_AT_KEY, String(time));
    } catch {
        // Storage blocked (private mode, policies): reload anyway; the guard is best effort.
    }
};

/** Indirection so tests can observe navigation; jsdom's `location.assign` can't be spied on. */
export const chunkReload = {
    navigate: (url: string) => window.location.assign(url),
};

/**
 * Do the one full page load, unless we already did within the guard window.
 * Returns whether a reload was started.
 */
export const reloadForNewBuild = (targetUrl: string = window.location.href) => {
    const now = Date.now();
    if (now - readReloadedAt() < RELOAD_GUARD_MS) return false;
    writeReloadedAt(now);
    chunkReload.navigate(targetUrl);
    return true;
};

/**
 * Wrap a dynamic import so a missing chunk reloads onto the new build. While the reload is under
 * way the returned promise never settles, so nothing renders an error for a page that's about to
 * be replaced. Any other error, or a chunk error inside the guard window, is rethrown.
 *
 *     const loadDeck = () => importWithReload(() => import('@deck.gl/google-maps'));
 */
export const importWithReload = <T>(load: () => Promise<T>): Promise<T> =>
    load().catch(error => {
        if (isChunkLoadError(error) && reloadForNewBuild()) {
            return new Promise<T>(() => undefined);
        }
        throw error;
    });

/**
 * Vite fires `vite:preloadError` on window when a dependency it preloads for a dynamic import
 * fails. Call once at startup. `preventDefault()` stops Vite rethrowing while we reload.
 */
export const installChunkReloadHandler = () => {
    const onPreloadError = (event: Event & { payload?: unknown }) => {
        if (reloadForNewBuild()) event.preventDefault();
    };
    window.addEventListener('vite:preloadError', onPreloadError);
    return () => window.removeEventListener('vite:preloadError', onPreloadError);
};
